import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Accordion, Box, Copy, Icon, Link, useAccordion } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import WarrantyOptions from './WarrantyOptions';
import WarrantyOptionsModal from './WarrantyOptionsModal';
import themeStyles from './WarrantyOptions.styles';
import useBapCtaClickGtm from '../../core/gtm/hooks/useBapCtaClickGtm';
import useBapRegisterUserInteraction from '../../utils/hooks/bap/useBapRegisterUserInteraction';
import useWarranties from './useWarranties';
import { WarrantyOptionTypes, WarrantyTypes } from '../../core/types';
import { addGlobalToastNotification, selectIsWarrantyOptionsModalOpen, selectWarrantyOptionsModalKey, selectWarrantyOptionsModalType, selectWarrantyOptionsRecommendedWarranty, setWarrantyOptionsModalKey, setWarrantyOptionsModalType, setWarrantyOptionsRecommendedWarranty, showRecommendedWarrantyChangedToast, toggleWarrantyOptionsModal, } from '../../core/reducers/ui';
import { createAndHydrateCalculator, defaultDraftCalculatorId, primaryCalculatorId, selectCheckedModalWarrantyCard, warrantyChangedForPrimaryCalculator, } from '../../core/reducers/financialCalculators';
import { useAppDispatch, useAppSelector } from '../../core/utils';
const Container = themeStyles.apply(Box, 'Container');
const WarrantyOptionsTitle = themeStyles.apply(Copy, 'WarrantyOptionsTitle');
const WarrantyOptionsSummary = ({ rendering }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const ctaText = t('Pages.Bap.viewMoreOptionsButton');
    const isModalOpen = useAppSelector(selectIsWarrantyOptionsModalOpen);
    const { fireGtmEvent } = useBapCtaClickGtm();
    const { registerUserInteraction } = useBapRegisterUserInteraction('warranty');
    const warrantiesMounted = useRef(false);
    const prevRecommendedWarrantyKey = useRef(null);
    const { allWarranties, extendedWarranties, recommendedWarranty, flexibleWarranties, totalWarrantyCostText, selectedWarrantyKey, yearsWithAnnualKmOptions, getOptionTypeFromWarranty, } = useWarranties(primaryCalculatorId);
    const [lastVisibleTypes, setLastVisibleTypes] = useState([WarrantyOptionTypes.Recommended]);
    const [recommendedIsChanged, setRecommendedIsChanged] = useState(false);
    const [warrantyWasRecommended, setWarrantyWasRecommended] = useState(false);
    const [checkedWarrantyCard, setCheckedWarrantyCard] = useState('');
    const recommendedWarrantyKey = recommendedWarranty === null || recommendedWarranty === void 0 ? void 0 : recommendedWarranty.businessKey;
    let visibleTypes = [WarrantyOptionTypes.Recommended];
    const selectedWarranty = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.find(warranty => warranty.businessKey === selectedWarrantyKey);
    const checkedModalWarranty = useAppSelector(state => selectCheckedModalWarrantyCard(state, primaryCalculatorId));
    const modalWarrantyType = useAppSelector(state => selectWarrantyOptionsModalType(state));
    const stateRecommendedWarranty = useAppSelector(state => selectWarrantyOptionsRecommendedWarranty(state));
    const selectedOptionType = getOptionTypeFromWarranty(selectedWarranty, checkedModalWarranty, modalWarrantyType);
    const [modalWarrantyKey, setModalWarrantyKey] = useState();
    // handles recommended change alerts
    useEffect(() => {
        if (!warrantiesMounted.current) {
            if (recommendedWarrantyKey === undefined)
                return;
            warrantiesMounted.current = true;
        }
        // this handles the scenario where we leave the summary page on bap, but we want to save the recommended key
        // prevents false positive of recommended changing when going back to summary page
        if (!!stateRecommendedWarranty && stateRecommendedWarranty === recommendedWarrantyKey) {
            return;
        }
        dispatch(setWarrantyOptionsRecommendedWarranty(recommendedWarrantyKey || ''));
        const recommendedWarrantyActuallyChanged = !!prevRecommendedWarrantyKey.current &&
            !!recommendedWarrantyKey &&
            prevRecommendedWarrantyKey.current !== recommendedWarrantyKey;
        if (recommendedWarrantyActuallyChanged) {
            setRecommendedIsChanged(true);
            dispatch(showRecommendedWarrantyChangedToast({
                id: uuid(),
                message: t('Pages.Bap.warrantyChangedMessageText'),
            }));
        }
        // Update previous key reference for next render
        prevRecommendedWarrantyKey.current = recommendedWarrantyKey !== null && recommendedWarrantyKey !== void 0 ? recommendedWarrantyKey : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendedWarrantyKey]);
    const modalKey = useAppSelector(state => selectWarrantyOptionsModalKey(state));
    const selectedModalWarranty = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.find(warranty => warranty.businessKey === modalKey);
    if (selectedOptionType === WarrantyOptionTypes.Extended) {
        // recomendedIsChanged checks added to display recommended warranty as well when
        // the aforementioned condition occurs
        if (recommendedIsChanged && recommendedWarrantyKey) {
            visibleTypes = [WarrantyOptionTypes.Recommended, WarrantyOptionTypes.Extended];
        }
        else {
            visibleTypes = [WarrantyOptionTypes.Extended];
        }
    }
    else if (selectedOptionType === WarrantyOptionTypes.Flexible) {
        if (recommendedIsChanged && recommendedWarrantyKey) {
            visibleTypes = [WarrantyOptionTypes.Recommended, WarrantyOptionTypes.Flexible];
        }
        else {
            visibleTypes = [WarrantyOptionTypes.Flexible];
        }
    }
    else if (selectedOptionType === WarrantyOptionTypes.None) {
        visibleTypes = lastVisibleTypes;
    }
    const getWarrantyTypeTranslation = (type) => {
        switch (type) {
            case WarrantyTypes.Recommended:
                return t('Pages.Bap.recommendedLabel');
            case WarrantyTypes.Extended:
                return t('Pages.Bap.extendedLabel');
            case WarrantyTypes.Flexible:
                return t('Pages.Bap.flexibleLabel');
            default:
                return '';
        }
    };
    const handleTypeSelectionChanged = (newType) => {
        const prevType = selectedOptionType;
        let newWarranty;
        if (newType === WarrantyOptionTypes.None) {
            dispatch(warrantyChangedForPrimaryCalculator('', 0));
            setWarrantyWasRecommended(false);
            setModalWarrantyKey('');
            setCheckedWarrantyCard('');
        }
        else if (newType === WarrantyOptionTypes.Extended) {
            setWarrantyWasRecommended(false);
            setCheckedWarrantyCard(newType);
            if ((selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.years) && (selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.annualKms)) {
                newWarranty = extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => w.years === (selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.years) && w.annualKms === (selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.annualKms));
            }
            else if ((selectedModalWarranty === null || selectedModalWarranty === void 0 ? void 0 : selectedModalWarranty.years) && selectedModalWarranty.annualKms) {
                newWarranty = extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => w.years === (selectedModalWarranty === null || selectedModalWarranty === void 0 ? void 0 : selectedModalWarranty.years) && w.annualKms === (selectedModalWarranty === null || selectedModalWarranty === void 0 ? void 0 : selectedModalWarranty.annualKms));
            }
            else {
                newWarranty = extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => {
                    var _a, _b, _c;
                    return w.years === ((_a = yearsWithAnnualKmOptions === null || yearsWithAnnualKmOptions === void 0 ? void 0 : yearsWithAnnualKmOptions[0]) === null || _a === void 0 ? void 0 : _a.years) &&
                        w.annualKms === ((_c = (_b = yearsWithAnnualKmOptions === null || yearsWithAnnualKmOptions === void 0 ? void 0 : yearsWithAnnualKmOptions[0]) === null || _b === void 0 ? void 0 : _b.annualKms) === null || _c === void 0 ? void 0 : _c[0]);
                });
            }
        }
        else if (newType === WarrantyOptionTypes.Flexible) {
            setWarrantyWasRecommended(false);
            setCheckedWarrantyCard(newType);
            if (selectedWarrantyKey) {
                newWarranty = flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties.find(w => w.businessKey === selectedWarrantyKey);
            }
            else if (selectedModalWarranty) {
                newWarranty = flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties.find(w => w.businessKey === modalKey);
            }
            else if (flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties[0]) {
                newWarranty = flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties[0];
            }
        }
        else if (newType === WarrantyOptionTypes.Recommended && recommendedWarranty) {
            newWarranty = recommendedWarranty;
            setCheckedWarrantyCard(newType);
            setWarrantyWasRecommended(true);
        }
        if (newWarranty && newWarranty.businessKey) {
            dispatch(setWarrantyOptionsModalType(newType));
            dispatch(warrantyChangedForPrimaryCalculator(newWarranty.businessKey, newWarranty.priceTotal, newType, newWarranty.businessKey));
        }
        // we want to prevent add and remove from firing if we switch payment options while recommended warranty is checked
        // therefore we add this condition to account for the recommended -> recommended scenario
        const newWarrantyIsAlsoRecommended = modalWarrantyType === WarrantyTypes.Recommended &&
            newType === WarrantyOptionTypes.Recommended &&
            recommendedIsChanged &&
            warrantyWasRecommended;
        if (prevType !== newType && !newWarrantyIsAlsoRecommended) {
            setLastVisibleTypes(visibleTypes);
            if (newType === WarrantyOptionTypes.None || prevType !== WarrantyOptionTypes.None) {
                dispatch(addGlobalToastNotification({
                    id: uuid(),
                    message: t('Pages.Bap.warrantyRemovedMessageText', {
                        warrantyType: getWarrantyTypeTranslation((modalWarrantyType == WarrantyTypes.Extended && !warrantyWasRecommended) ||
                            modalWarrantyType == WarrantyTypes.Recommended ||
                            (modalWarrantyType == WarrantyTypes.Flexible && !warrantyWasRecommended)
                            ? modalWarrantyType
                            : selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.bapType),
                    }),
                }));
            }
            if (newType !== WarrantyOptionTypes.None) {
                dispatch(addGlobalToastNotification({
                    id: uuid(),
                    message: t('Pages.Bap.warrantyAddedMessageText', {
                        warrantyType: getWarrantyTypeTranslation((modalWarrantyType == WarrantyTypes.Extended && !warrantyWasRecommended) ||
                            modalWarrantyType == WarrantyTypes.Recommended ||
                            (modalWarrantyType == WarrantyTypes.Flexible && !warrantyWasRecommended)
                            ? newType
                            : // newWarranty?.bapType prevents error where blank warrant value is alerted
                                (selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.bapType) || (newWarranty === null || newWarranty === void 0 ? void 0 : newWarranty.bapType)),
                    }),
                }));
            }
        }
    };
    const handleExtendedWarrantyYearsChanged = (years) => {
        var _a;
        const yearsWithAnnualKms = yearsWithAnnualKmOptions.find(o => o.years === years);
        const annualKmsValue = (_a = yearsWithAnnualKms === null || yearsWithAnnualKms === void 0 ? void 0 : yearsWithAnnualKms.annualKms) === null || _a === void 0 ? void 0 : _a[0];
        const warranty = extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => w.years === years && w.annualKms === annualKmsValue);
        if (selectedOptionType === WarrantyOptionTypes.Extended && warranty && warranty.businessKey) {
            dispatch(warrantyChangedForPrimaryCalculator(warranty === null || warranty === void 0 ? void 0 : warranty.businessKey, warranty.priceTotal));
        }
    };
    const handleExtendedWarrantyAnnualKmsChanged = (annualKms) => {
        const warranty = extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => w.years === (selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.years) && w.annualKms === annualKms);
        if (selectedOptionType === WarrantyOptionTypes.Extended && warranty && warranty.businessKey) {
            dispatch(warrantyChangedForPrimaryCalculator(warranty === null || warranty === void 0 ? void 0 : warranty.businessKey, warranty.priceTotal));
        }
    };
    const handleFlexibleWarrantySelectionChanged = (warrantyKey) => {
        var _a;
        if (selectedOptionType === WarrantyOptionTypes.Flexible) {
            dispatch(warrantyChangedForPrimaryCalculator(warrantyKey, (_a = flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties.find(w => w.businessKey === warrantyKey)) === null || _a === void 0 ? void 0 : _a.priceTotal));
        }
    };
    const handleModalSubmit = (warrantyKey, checkedModalWarrantyCard, warrantyModalKey) => {
        if (selectedWarrantyKey !== warrantyKey || checkedModalWarranty !== checkedModalWarrantyCard) {
            registerUserInteraction();
            const newWarranty = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.find(warranty => warranty.businessKey === warrantyKey);
            setRecommendedIsChanged(false);
            setWarrantyWasRecommended(false);
            if (warrantyKey && newWarranty) {
                dispatch(addGlobalToastNotification({
                    id: uuid(),
                    message: t('Pages.Bap.warrantyAddedMessageText', {
                        warrantyType: getWarrantyTypeTranslation(checkedModalWarrantyCard == WarrantyTypes.Extended ||
                            checkedModalWarrantyCard == WarrantyTypes.Recommended ||
                            checkedModalWarrantyCard == WarrantyTypes.Flexible
                            ? checkedModalWarrantyCard
                            : selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.bapType),
                    }),
                }));
            }
            else {
                dispatch(addGlobalToastNotification({
                    id: uuid(),
                    message: t('Pages.Bap.warrantyRemovedMessageText', {
                        warrantyType: getWarrantyTypeTranslation(checkedModalWarrantyCard == WarrantyTypes.Extended ||
                            checkedModalWarrantyCard == WarrantyTypes.Recommended ||
                            checkedModalWarrantyCard == WarrantyTypes.Flexible
                            ? checkedModalWarrantyCard
                            : selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.bapType),
                    }),
                }));
            }
            dispatch(setWarrantyOptionsModalKey(warrantyKey || ''));
            dispatch(setWarrantyOptionsModalType((checkedWarrantyCard || WarrantyOptionTypes.None)));
            dispatch(warrantyChangedForPrimaryCalculator(warrantyKey || '', newWarranty === null || newWarranty === void 0 ? void 0 : newWarranty.priceTotal, checkedWarrantyCard, warrantyModalKey || ''));
        }
        dispatch(toggleWarrantyOptionsModal(false));
    };
    const handleModalCancel = () => {
        dispatch(toggleWarrantyOptionsModal(false));
    };
    // updates selected recommended warranty if it's the selected option while the recommended key is changed
    useEffect(() => {
        if (warrantyWasRecommended && selectedOptionType !== WarrantyOptionTypes.Recommended) {
            handleTypeSelectionChanged(WarrantyOptionTypes.Recommended);
            dispatch(setWarrantyOptionsModalType(WarrantyOptionTypes.Recommended));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptionType]);
    const accordionItems = [
        {
            key: 'warranty-options',
            title: (_jsxs(WarrantyOptionsTitle, { "data-testid": "cy-warranty-options-title", children: [_jsx(Copy, { style: { display: 'flex', flex: 1 }, fontWeight: "bold", size: "subheading", as: "h5", "data-testid": "cy-warranty-options-title-header", children: t('Pages.Bap.warrantyOptionsLabel') }), _jsx(Copy, { style: { flex: 0 }, fontWeight: "bold", "data-testid": "total-warranty-cost", children: totalWarrantyCostText })] })),
            content: (_jsxs(Box, { children: [_jsx(WarrantyOptions, { showTypes: visibleTypes, selectedOptionType: selectedOptionType, selectedFlexibleWarrantyKey: selectedWarrantyKey, selectedYearsValue: selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.years, selectedAnnualKmsValue: selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.annualKms, onTypeSelectionChanged: option => {
                            registerUserInteraction();
                            handleTypeSelectionChanged(option);
                        }, onExtendedWarrantyYearsChanged: handleExtendedWarrantyYearsChanged, onExtendedWarrantyAnnualKmsChanged: handleExtendedWarrantyAnnualKmsChanged, onFlexibleWarrantySelectionChanged: handleFlexibleWarrantySelectionChanged }), _jsx(Box, { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsxs(Link, { href: true, onClick: () => {
                                dispatch(createAndHydrateCalculator(primaryCalculatorId, defaultDraftCalculatorId));
                                dispatch(toggleWarrantyOptionsModal(true));
                                fireGtmEvent({
                                    ctaType: 'explore',
                                    ctaPlacement: 'summary : warranty',
                                    linkText: 'view more options',
                                    componentType: rendering === null || rendering === void 0 ? void 0 : rendering.componentName,
                                });
                            }, styling: "tertiary2", size: "regular", ml: "s", "data-testid": "cy-warranty-view-more-options", children: [ctaText, " ", _jsx(Icon, { ml: "12px", height: "12px", name: "arrowRight", iconColor: "blue" })] }) })] })),
        },
    ];
    const accordionBehaviour = useAccordion(accordionItems, undefined, ['warranty-options']);
    const behaviour = {
        ...accordionBehaviour,
        toggleItem: (key) => {
            const wasPrevExpanded = accordionBehaviour.activeItems.has(key);
            accordionBehaviour.toggleItem(key);
            fireGtmEvent({
                ctaType: `${wasPrevExpanded ? 'collapse' : 'expand'} accordion`,
                ctaPlacement: 'summary : warranty',
                componentType: rendering === null || rendering === void 0 ? void 0 : rendering.componentName,
            });
        },
    };
    return (_jsxs(_Fragment, { children: [_jsx(Container, { "data-testid": "cy-warranty-options-summary", children: _jsx(Accordion, { borderColor: "grey.2", isCompact: true, itemOverflow: true, activeTitleHasBorder: true, itemsHaveTopBorder: false, hideBorderOnLastItem: false, contentContainerStyles: { py: 'xs' }, items: accordionItems, behaviour: behaviour, overrideAccessibility: true }) }), _jsx(WarrantyOptionsModal, { title: t('Pages.Bap.warrantyOptionsLabel'), description: t('Pages.Bap.warrantyOptionsBlurbText'), isOpen: isModalOpen, submitButtonText: t('Pages.Bap.okButton'), modalWarrantyKey: modalWarrantyKey, setModalWarrantyKey: setModalWarrantyKey, onSubmit: handleModalSubmit, onCancel: handleModalCancel, checkedWarrantyCard: checkedWarrantyCard, setCheckedWarrantyCard: setCheckedWarrantyCard })] }));
};
export default WarrantyOptionsSummary;
